import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Modal } from "@/shared/ui/modal";

import { useResetPassword } from "../api/use-reset-password";

type AdminUser = components["schemas"]["AdminUser"];

interface ResetPasswordDialogProps {
  user: AdminUser;
  onClose: () => void;
}

export function ResetPasswordDialog({ user, onClose }: ResetPasswordDialogProps): JSX.Element {
  const { t } = useTranslation("users");
  const reset = useResetPassword();
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  function onConfirm(): void {
    reset.mutate(user.id, {
      onSuccess: (data) => setTempPassword(data.temp_password),
      onError: (err) => toast.error(apiErrorMessage(err)),
    });
  }

  if (tempPassword) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t("reset_password.success_title")}
        footer={
          <Button onClick={onClose} data-testid="reset-password-done">
            {t("reset_password.close")}
          </Button>
        }
      >
        <p className="mb-3 text-sm text-muted-foreground">
          {t("reset_password.success_description", { name: user.display_name })}
        </p>
        <code
          className="block rounded bg-muted px-3 py-2 text-sm"
          data-testid="reset-password-temp"
        >
          {tempPassword}
        </code>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t("reset_password.title", { name: user.display_name })}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={reset.isPending}
            data-testid="reset-password-cancel"
          >
            {t("action.cancel")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={reset.isPending}
            data-testid="reset-password-confirm"
          >
            {t("reset_password.confirm")}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">{t("reset_password.description")}</p>
    </Modal>
  );
}
